import { Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { tDeploymentScope } from "@/i18n/deploymentScopeStrings";

/**
 * Small info button rendered next to the device-count suffix in the wizard
 * "Deployment scope" row. Explains how the displayed number is derived.
 *
 * Modes (chosen by the host wizard, not auto-detected):
 *  - `mode="external"` (default): single-tag bodies "Exact count: …",
 *    multi-tag bodies "Upper bound: …". Renders nothing when
 *    `selectedTagCount === 0` so the row stays clean while the user is
 *    still picking tags.
 *  - `mode="catalog"`: a single body explaining that the configuration's
 *    scope follows the parent app and tags don't apply. Always renders
 *    regardless of `selectedTagCount` — catalog rows have no per-tag
 *    arithmetic to gate on.
 *
 * Passing the mode explicitly (rather than inferring from the tag count)
 * keeps the tooltip honest for catalog targets, which can momentarily have
 * `selectedTagCount === 0` for completely different reasons than an
 * external target with no tags picked.
 *
 * Trigger is a real <button> — keyboard focus shows the tooltip via the
 * shadcn/Radix tooltip's built-in focus handling.
 */
export const DeviceCountInfoTooltip = ({
  selectedTagCount,
  mode = "external",
}: {
  selectedTagCount: number;
  mode?: "external" | "catalog";
}) => {
  if (mode === "external" && selectedTagCount === 0) return null;

  const body =
    mode === "catalog"
      ? tDeploymentScope("deviceCountTooltipCatalog")
      : tDeploymentScope(
          selectedTagCount >= 2
            ? "deviceCountTooltipMultiTag"
            : "deviceCountTooltipSingleTag",
        );
  const label = tDeploymentScope("deviceCountTooltipLabel");

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={label}
            data-testid="deployment-scope-device-count-info"
            data-mode={mode}
            className="inline-flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
          >
            <Info className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" align="end" className="max-w-xs text-xs">
          {body}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
